import React, { useContext, useEffect } from 'react';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import { DateFormats } from 'constants/common';
import { CircularProgress, Stack } from '@mui/material';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { ReportInfo } from './ReportInfo';
import { CobaltIntelligenceCardFallback } from './CobaltIntelligenceCardFallback';
import { cobaltIntelligenceApi, LastSecretaryOfStateReportForAccountResponse } from '../api';

interface Props {
  voId: string;
}

export const CobaltIntelligenceCard = ({ voId }: Props) => {
  const { isSuperAdmin } = useContext(CommonDataContext);

  const { loading, data, error, previousData, startPolling, stopPolling } =
    useQuery<LastSecretaryOfStateReportForAccountResponse>(
      cobaltIntelligenceApi.getLastSecretaryOfStateReportForAccount,
      { variables: { input: { voId } } }
    );

  const viewModel = (data ?? previousData)?.cobaltIntelligence?.result?.entity;
  const isGenerateReportAvailable = viewModel ? viewModel.canGenerateReport : true;

  const generateReportTooltip = isGenerateReportAvailable
    ? ''
    : `Next call available after: ${moment(viewModel?.nextAvailableAt).format(DateFormats.DateTimeMedium24Hour)}`;

  useEffect(() => {
    if (!isGenerateReportAvailable) {
      startPolling(60_000);
    } else {
      stopPolling();
    }
  }, [isGenerateReportAvailable, startPolling, stopPolling]);

  return (
    <CardContainer title="KYB verification" style={{ overflow: 'inherit' }}>
      <RenderWithFallback
        error={error}
        isLoading={loading && !viewModel}
        fallBack={
          <Stack height="100%" minHeight="10rem" width="100%" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        }
      >
        <RenderWithCondition
          condition={!!viewModel}
          fallBack={
            <CobaltIntelligenceCardFallback
              voId={voId}
              isSuperAdmin={isSuperAdmin}
              isGenerateReportAvailable={isGenerateReportAvailable}
              generateReportTooltip={generateReportTooltip}
            />
          }
        >
          <ReportInfo
            voId={voId}
            viewModel={viewModel!}
            isSuperAdmin={isSuperAdmin}
            generateReportTooltip={generateReportTooltip}
            isGenerateReportAvailable={isGenerateReportAvailable}
          />
        </RenderWithCondition>
      </RenderWithFallback>
    </CardContainer>
  );
};
